import { cn } from '@lensora/ui'
import { Info } from 'lucide-react'
import * as React from 'react'

export interface SubScore {
  id: string
  label: string
  score: number
  weight: number
  description: string
  color: string
}

// Ring radii step inward — outermost ring is reserved for Competitor Gap,
// the rest fill in reverse-weight order. Mirrors the concentric layout
// used by PlatformRingsWidget.
const RING_RADII = [104, 88, 72, 56, 40] as const
const STROKE_WIDTH = 9
const CENTER = 120
const SVG_SIZE = 240

// Draws a ring as an explicit arc path (rather than stroke-dasharray) so a
// 100% score always renders as a genuinely closed circle — dasharray math
// can leave a visible seam at the full-circle edge case due to how the
// browser rounds circumference vs. the round linecap.
function describeArc(pct: number, radius: number): string {
  const clamped = Math.max(0, Math.min(100, pct))
  if (clamped >= 100) {
    // A single <path> arc command can't express a full 360° sweep (degenerate
    // start === end), so draw it as two 180° arcs instead.
    return [
      `M ${CENTER} ${CENTER - radius}`,
      `A ${radius} ${radius} 0 1 1 ${CENTER} ${CENTER + radius}`,
      `A ${radius} ${radius} 0 1 1 ${CENTER} ${CENTER - radius}`,
    ].join(' ')
  }
  const startAngle = -Math.PI / 2
  const endAngle = startAngle + (clamped / 100) * 2 * Math.PI
  const x1 = CENTER + radius * Math.cos(startAngle)
  const y1 = CENTER + radius * Math.sin(startAngle)
  const x2 = CENTER + radius * Math.cos(endAngle)
  const y2 = CENTER + radius * Math.sin(endAngle)
  const largeArc = clamped > 50 ? 1 : 0
  return `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`
}

interface SubScoreRingsWidgetProps {
  subScores: SubScore[]
  overallScore: number
}

export function SubScoreRingsWidget({ subScores, overallScore }: SubScoreRingsWidgetProps) {
  const [hovered, setHovered] = React.useState<string | null>(null)
  const [tooltip, setTooltip] = React.useState<string | null>(null)

  const active = hovered ? subScores.find((s) => s.id === hovered) : null

  // Competitor Gap drawn outermost; remaining scores nest inward in their existing order.
  const competitorGap = subScores.find((s) => s.id === 'competitor_gap')
  const rest = subScores.filter((s) => s.id !== 'competitor_gap')
  const ringOrder = competitorGap ? [competitorGap, ...rest] : subScores

  return (
    <div className="rounded-2xl border border-border bg-surface p-3.5">
      <p className="mb-0.5 text-[11px] font-semibold uppercase tracking-wide text-text-muted">
        Score breakdown
      </p>
      <p className="mb-2.5 text-[11px] text-text-muted">
        How each dimension contributes to your overall score
      </p>

      <div className="flex items-center gap-5">
        <svg width={SVG_SIZE} height={SVG_SIZE} viewBox={`0 0 ${SVG_SIZE} ${SVG_SIZE}`} className="shrink-0">
          {ringOrder.map((s, i) => {
            const r = RING_RADII[i] ?? RING_RADII[4]
            const isActive = hovered === null || hovered === s.id
            return (
              <g key={s.id} className="transition-opacity duration-200" style={{ opacity: isActive ? 1 : 0.15 }}>
                <circle cx={CENTER} cy={CENTER} r={r} fill="none" className="stroke-border" strokeWidth={STROKE_WIDTH} />
                <path
                  d={describeArc(s.score, r)}
                  fill="none"
                  stroke={s.color}
                  strokeWidth={STROKE_WIDTH}
                  strokeLinecap="round"
                />
              </g>
            )
          })}
          <text x={CENTER} y={CENTER - 6} textAnchor="middle" fontSize="30" fontWeight="700" className="fill-text tabular-nums">
            {active ? active.score : overallScore}
          </text>
          <text x={CENTER} y={CENTER + 18} textAnchor="middle" fontSize="12" className="fill-text-muted">
            {active ? active.label : 'Overall'}
          </text>
        </svg>

        <div className="flex w-64 flex-col gap-1">
          {subScores.map((s) => (
            <div
              key={s.id}
              onMouseEnter={() => setHovered(s.id)}
              onMouseLeave={() => setHovered(null)}
              className={cn(
                'flex cursor-pointer items-center gap-2 rounded-md px-1.5 py-1 transition-colors',
                hovered === s.id && 'bg-surface-overlay'
              )}
            >
              <div className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: s.color }} />
              <span className="truncate text-xs text-text">{s.label}</span>
              <div className="relative shrink-0">
                <button
                  onMouseEnter={(e) => { e.stopPropagation(); setTooltip(s.id) }}
                  onMouseLeave={(e) => { e.stopPropagation(); setTooltip(null) }}
                  className="text-text-muted transition-colors hover:text-text"
                  aria-label={`About ${s.label} score`}
                >
                  <Info className="h-3 w-3" />
                </button>
                {tooltip === s.id && (
                  <div className="absolute left-0 top-5 z-10 w-48 rounded-lg border border-border bg-surface p-2.5 text-[11px] leading-relaxed text-text-muted shadow-lg">
                    {s.description}
                  </div>
                )}
              </div>
              <span className="flex-1 text-right text-xs font-semibold tabular-nums" style={{ color: s.color }}>
                {s.score}
              </span>
              <span className="w-20 shrink-0 text-right text-[10px] text-text-muted">{s.weight}% weight</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
